import SecureLS from 'secure-ls';
import Cookies from 'cookies-ts';

import {
  COOKIE_NAMES,
  GEOLOCATION_EXPIRATION_KEY,
  GEOLOCATION_EXPIRATION_TIME,
  GeolocationType
} from './global-storage';

export const secureStorage = new SecureLS({ encodingType: 'aes' });

const MAPS_API_KEY =
  (document.querySelector('[data-api]') as HTMLElement)?.dataset?.api ||
  'AIzaSyBiAlQV0Vq0CdAR6on522xLjaMF8iroYPU';

export const AKAMAI_GEO_COOKIE_NAME = 'ak_geo';

const DEFAULT_AUTODETECTED_GEOLOCATION = {
  postal: '07054',
  country: 'US'
} as const;

export type AkamaiGeolocationData = {
  postal?: string;
  city?: string;
  fips?: string;
  region?: string;
  country?: string;
  dma?: string;
  msa?: string;
  pmsa?: string;
  latitude?: number;
  longitude?: number;
};

//Check if there is a location in local storage or cookies
export const getStoredGeolocationData = (): GeolocationType | null => {
  const raw = secureStorage.get(COOKIE_NAMES.geolocation) as string | null;
  if (!raw) return null;

  try {
    return JSON.parse(raw) as GeolocationType;
  } catch {
    return null;
  }
};

//Get address from geolocation stored in local storage or cookies
export const getAddressFromGeolocation = () => {
  let address: string | null = null;
  const fromLocalStorage = secureStorage.get(COOKIE_NAMES.geolocation);

  if (fromLocalStorage) {
    const geolocationObj = JSON.parse(fromLocalStorage) as GeolocationType;
    if (geolocationObj.address) address = geolocationObj.address;
  }

  return address;
};

//Get the location data from the lat long using google maps api

export const getLocationDataFromLatLong = async (
  latitude: number,
  longitude: number
) => {
  const response = await window.fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${MAPS_API_KEY}`
  );

  const dataLatLong = await response.json();

  const zipFromData = dataLatLong.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('postal_code')
  );
  const countryFromData = dataLatLong.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('country')
  );

  if (!zipFromData) {
    return {
      zip: undefined,
      country: undefined
    };
  }

  if (zipFromData.short_name && countryFromData.short_name) {
    return {
      zip: zipFromData.short_name,
      country: countryFromData.short_name
    };
  } else {
    return {
      zip: undefined,
      country: undefined
    };
  }
};

//Get the location data from the address using google maps api

export const getLocationDataFromAddress = async (address: string) => {
  const response = await window.fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${MAPS_API_KEY}`
  );

  const dataLocation = await response.json();
  //return if location is not valid
  if (!dataLocation || dataLocation?.status === 'ZERO_RESULTS') {
    return {
      zip: undefined,
      country: undefined,
      latitude: undefined,
      longitude: undefined,
      city: undefined,
      street: undefined,
      streetNumber: undefined,
      state: undefined,
      secondStreet: undefined
    };
  }

  const zipFromData = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('postal_code')
  );
  const countryFromData = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('country')
  );
  const latitude = dataLocation.results[0].geometry.location.lat;
  const longitude = dataLocation.results[0].geometry.location.lng;

  const city = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('locality')
  );
  const street = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('route')
  );

  const secondStreet = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('subpremise')
  );
  const state = dataLocation.results[0].address_components.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => component.types.includes('administrative_area_level_1')
  );

  const streetNumber = dataLocation.results[0].address_components?.find(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component: any) => {
      return component.types.includes('street_number');
    }
  );

  if (zipFromData?.short_name && countryFromData?.short_name) {
    return {
      zip: zipFromData?.short_name || '',
      country: countryFromData?.short_name || '',
      latitude,
      longitude,
      city: city?.long_name || '',
      street: street?.long_name || '',
      secondStreet: secondStreet?.long_name || '',
      state: state?.long_name || '',
      streetNumber: streetNumber?.long_name || ''
    };
  } else {
    return {
      zip: undefined,
      country: undefined,
      latitude: undefined,
      longitude: undefined,
      city: undefined,
      street: undefined,
      state: undefined,
      streetNumber: undefined,
      secondStreet: undefined
    };
  }
};

//Get the latitude and longitude from the zip code using google maps api

export const getLatLongFromZip = async (zip: string, country: string) => {
  let countryCode = country;

  if (country.length === 3) {
    countryCode = country.slice(0, -1);
  }

  // Puerto Rico uses US-format ZIP codes (00600–00988) but is a separate country in Google's geocoding API.
  // Override the country code so the lookup resolves correctly.
  if (countryCode.toLowerCase().trim() === 'us' && /^00[6-9]\d\d$/.test(zip)) {
    countryCode = 'PR';
  }

  const response = await window.fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?key=${MAPS_API_KEY}&components=postal_code:${zip}|country:${countryCode}`
  );

  const data = await response.json();

  if (data.status === 'OK') {
    const { lat, lng } = data.results[0].geometry.location;
    const formattedAddress = data.results[0].formatted_address;

    return {
      latitude: lat,
      longitude: lng,
      address: formattedAddress
    };
  }

  window.dispatchEvent(new Event(INVALID_GOOGLE_ZIP_EVENT));
  return {
    latitude: undefined,
    longitude: undefined,
    address: undefined
  };
};

type UnknownRecord = Record<string, unknown>;

const getCookieString = (
  value: unknown,
  maxLength = 100
): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength) return undefined;

  return normalized;
};

const getCoordinate = (
  value: unknown,
  minimum: number,
  maximum: number
): number | undefined => {
  let coordinate = Number.NaN;

  if (typeof value === 'number') {
    coordinate = value;
  } else if (typeof value === 'string' && value.trim()) {
    coordinate = Number(value);
  }

  return Number.isFinite(coordinate) &&
    coordinate >= minimum &&
    coordinate <= maximum
    ? coordinate
    : undefined;
};

const parseAkamaiCookieValue = (value: unknown): UnknownRecord | null => {
  let parsedValue = value;

  if (typeof parsedValue === 'string') {
    try {
      parsedValue = JSON.parse(parsedValue) as unknown;
    } catch {
      return null;
    }
  }

  if (
    !parsedValue ||
    typeof parsedValue !== 'object' ||
    Array.isArray(parsedValue)
  ) {
    return null;
  }

  return parsedValue as UnknownRecord;
};

const isValidAkamaiPostalCode = (postal: string, country?: string) => {
  if (country === 'US' || country === 'PR') return /^\d{5}$/.test(postal);
  if (country === 'CA') return /^[A-Z]\d[A-Z]$/.test(postal);

  return /^[A-Z0-9][A-Z0-9 ]{0,15}$/.test(postal);
};

/**
 * Akamai uses + between independent postal values and - between the endpoints
 * of a contiguous range. Select the first range's left endpoint while keeping
 * the value as a string so leading zeroes are preserved.
 * https://techdocs.akamai.com/edgeworkers/docs/user-location-object#zipCode
 */
export const getFirstAkamaiPostalCode = (
  value: unknown,
  country?: string
): string | null => {
  if (typeof value !== 'string' || value.length > 500) return null;

  const countryCode = country?.toUpperCase();
  const postalGroups = value.toUpperCase().split('+');

  for (const postalGroup of postalGroups) {
    const range = postalGroup.split('-').map(postal => postal.trim());

    if (
      range.length > 2 ||
      range.some(
        postal => !postal || !isValidAkamaiPostalCode(postal, countryCode)
      )
    ) {
      continue;
    }

    return range[0];
  }

  return null;
};

export const getAkamaiGeolocationData = (): AkamaiGeolocationData | null => {
  let cookieValue: unknown;

  try {
    cookieValue = new Cookies().get(AKAMAI_GEO_COOKIE_NAME) as unknown;
  } catch {
    return null;
  }

  // Requests that do not pass through the Akamai edge do not receive ak_geo.
  if (!cookieValue) {
    return { ...DEFAULT_AUTODETECTED_GEOLOCATION };
  }

  const cookieData = parseAkamaiCookieValue(cookieValue);
  if (!cookieData) return null;

  const rawCountry = getCookieString(cookieData.country, 2);
  const country =
    rawCountry && /^[A-Z]{2}$/i.test(rawCountry)
      ? rawCountry.toUpperCase()
      : undefined;
  const postal =
    getFirstAkamaiPostalCode(cookieData.postal, country) || undefined;
  const latitude = getCoordinate(cookieData.lat, -90, 90);
  const longitude = getCoordinate(cookieData.long, -180, 180);
  const hasCoordinates = latitude !== undefined && longitude !== undefined;

  const geolocationData: AkamaiGeolocationData = {
    postal,
    city: getCookieString(cookieData.city),
    fips: getCookieString(cookieData.fips),
    region: getCookieString(cookieData.region, 10),
    country,
    dma: getCookieString(cookieData.dma),
    msa: getCookieString(cookieData.msa),
    pmsa: getCookieString(cookieData.pmsa),
    latitude: hasCoordinates ? latitude : undefined,
    longitude: hasCoordinates ? longitude : undefined
  };

  return Object.values(geolocationData).some(value => value !== undefined)
    ? geolocationData
    : null;
};

export const getLocationByAkamaiCookie = async (
  options: { requireCoordinates?: boolean } = {}
): Promise<GeolocationType | null> => {
  const data = getAkamaiGeolocationData();

  if (!data?.postal || !data.country) return null;

  let latitude = data.latitude;
  let longitude = data.longitude;
  let hasCoordinates = latitude !== undefined && longitude !== undefined;

  if (options.requireCoordinates && !hasCoordinates) {
    try {
      const geocodedLocation = await getLatLongFromZip(
        data.postal,
        data.country
      );
      latitude = geocodedLocation.latitude;
      longitude = geocodedLocation.longitude;
      hasCoordinates = latitude !== undefined && longitude !== undefined;
    } catch {
      return null;
    }
  }

  if (options.requireCoordinates && !hasCoordinates) return null;

  return {
    address: '',
    latitude,
    longitude,
    zip: data.postal,
    country: data.country.toLowerCase(),
    city: data.city,
    state: data.region
  };
};

//get postal code and country code from url

export const getZipAndCountryFromUrl = () => {
  const url = new URL(window.location.href);
  const zip =
    url.searchParams.get('postalCode') || url.searchParams.get('PostalCode');
  const country =
    url.searchParams.get('countryCode') || url.searchParams.get('CountryCode');

  if (zip && country) {
    return {
      zip,
      country: country.toLowerCase()
    };
  }

  return null;
};

//get postal code and country code from url and get lat long from google

export const INVALID_GOOGLE_ZIP_EVENT = 'invalid-google-zip';

export const getGeolocationDataFromUrlAndGoogle = async () => {
  const data = getZipAndCountryFromUrl();

  if (data && data.zip && data.country) {
    const { latitude, longitude } = await getLatLongFromZip(
      data.zip,
      data.country
    );

    if (latitude && longitude) {
      const locationData: GeolocationType = {
        latitude,
        longitude,
        zip: data.zip,
        country: data.country
      };

      return locationData;
    } else {
      setTimeout(() => {
        window.dispatchEvent(new Event(INVALID_GOOGLE_ZIP_EVENT));
      }, 500);

      return {
        latitude: undefined,
        longitude: undefined,
        zip: data.zip,
        country: data.country
      };
    }
  }

  return null;
};

export const COUNTRY_VALUES = ['us', 'ca', 'usa', 'can'];

//update a property in the geolocation object

export const updateGeolocationDataProperty = (key: string, value: string) => {
  let newGeolocationData: GeolocationType | null = null;
  const geolocationData = getStoredGeolocationData();

  if (geolocationData) {
    newGeolocationData = {
      ...geolocationData,
      [key]: value
    };
  } else {
    newGeolocationData = {
      country: '',
      zip: '',
      [key]: value
    } as GeolocationType;
  }

  secureStorage.set(
    COOKIE_NAMES.geolocation,
    JSON.stringify(newGeolocationData)
  );
};

//update multiple properties in the geolocation object

export const updateGeolocationDataProperties = (
  properties: Partial<GeolocationType>
) => {
  let newGeolocationData: GeolocationType | null = null;

  newGeolocationData = {
    ...properties
  } as GeolocationType;

  const now = new Date();
  const time = now.getTime() + GEOLOCATION_EXPIRATION_TIME;

  secureStorage.set(GEOLOCATION_EXPIRATION_KEY, time.toString());

  secureStorage.set(
    COOKIE_NAMES.geolocation,
    JSON.stringify(newGeolocationData)
  );
};
